import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

export function CountdownButton(props) {
	const durationInMilliseconds = props.duration * 1000; // Convert seconds to milliseconds
	const interval = 50; // Update every 50ms
	const [remainingTime, setRemainingTime] = useState(durationInMilliseconds);

	const degree = Math.round((360 / durationInMilliseconds) * remainingTime);
	const remainingTimeInSeconds = Math.ceil(remainingTime / 1000);

	useEffect(() => {
		let timer;

		if (remainingTime > 0) {
			timer = setInterval(() => {
				setRemainingTime((prevTime) => {
					if (prevTime <= 0) {
						clearInterval(timer);
						return 0;
					}
					return prevTime - interval;
				});
			}, interval);
		}

		return () => clearInterval(timer);
	}, []);

	useEffect(() => {
		if (remainingTime <= 0) {
			props.onClick?.();
		}
	}, [remainingTime]);

	const onCountdownButtonClick = () => setRemainingTime(0);

	return remainingTime > 0 ? (
		<Button {...props} primary={true} hasCountdown={true} onClick={onCountdownButtonClick}>
			{props.children}
			<span style={{ '--progress': `${degree}deg` }}>{remainingTimeInSeconds}s</span>
		</Button>
	) : (
		<LoadingButton {...props} primary={true} loading={true} />
	);
}

export function LoadingButton(props) {
	return <Button {...props} hasLoader={true} />;
}

export function PrimaryButton(props) {
	return <Button {...props} primary={true} />;
}

export function TertiaryButton(props) {
	return <Button {...props} tertiary={true} />;
}

export default function Button({
	children,
	disabled = false,
	hasCountdown = false,
	hasLoader = false,
	id = '',
	loading = false,
	onClick,
	primary = false,
	specifier = '',
	sso = false,
	ssoId = '',
	tertiary = false,
	testId = '',
}) {
	const props = {
		disabled,
		onClick,
	};

	const classes = ['btn'];
	primary && classes.push('btn--primary');
	tertiary && classes.push('btn--tertiary');
	specifier && classes.push(`btn--${specifier}`);

	hasCountdown && classes.push('btn--countdown');
	hasLoader && classes.push('btn--loader');
	loading && classes.push('btn--loading');

	// Handle SSO-specific classes
	if (sso) {
		classes.push('btn--sso');

		if (ssoId) {
			classes.push(`btn--sso--${ssoId}`);
		}
	}

	props.className = classes.join(' ');

	if (id) {
		props.id = id;
	}

	if (testId) {
		props['data-testid'] = testId;
	}

	return <button {...props}>{hasLoader ? <span>{children}</span> : children}</button>;
}

Button.propTypes = {
	children: PropTypes.node.isRequired,
	disabled: PropTypes.bool,
	hasCountdown: PropTypes.bool,
	hasLoader: PropTypes.bool,
	id: PropTypes.string,
	loading: PropTypes.bool,
	onClick: PropTypes.func.isRequired,
	primary: PropTypes.bool,
	specifier: PropTypes.string,
	sso: PropTypes.bool,
	ssoId: PropTypes.string,
	tertiary: PropTypes.bool,
	testId: PropTypes.string,
};

CountdownButton.propTypes = {
	children: PropTypes.node.isRequired,
	duration: PropTypes.number.isRequired,
	loading: PropTypes.bool,
	onClick: PropTypes.func.isRequired,
};
